import { EMPTY_STRING } from '@rotomeca/utils';
import { BnumConfig } from '../../../core/utils/BnumConfig';
import BnumElement from '../../bnum-element';
import { HTMLBnumCardElement } from '../card/bnum-card';
import { Nullable, Optional } from '@rotomeca/utils';
import style from './bnum-card-agenda.less';
import { HTMLBnumCardList } from '../../molecules/card-list/bnum-card-list';
import { HTMLBnumCardItem } from '../../molecules/card-item/bnum-card-item';
import { HTMLBnumCardTitle } from '../../molecules/card-title/bnum-card-title';
import { TAG_CARD_AGENDA } from '../../../core/utils/constants/tags';
import { HTMLBnumCardItemAgenda } from '../../molecules/card-item-agenda/bnum-card-item-agenda';
import { Attr, Data, Define } from '../../../core/decorators';
import { ArrayUtils } from '../../../core/utils/ArrayUtils';
import {
	ATTRIBUTE_DATA_URL,
	ATTRIBUTE_LOADING,
	CHANGE_EVENT,
	ID_CARD_ITEM_NO_ELEMENTS,
	ID_CARD_TITLE,
} from './bnum-card-agenda.internal/constants';
import { Listener } from 'core/decorators/listener';
import { onElementChangedInitializer } from './bnum-card-agenda.internal/listeners';
import { OnElementChangedEvent } from './bnum-card-agenda.internal/types';
import { UI } from 'core/decorators/ui';
import { RenderFrame } from 'core/decorators/defer';
import { Observe } from 'core/decorators/observe';
import { EVENT_DEFAULT } from 'core/utils/constants/constants';

//#region Global constants
const TEXT_LAST_EVENTS = BnumConfig.Get('local_keys')!.last_events!;
const TEXT_NO_EVENTS = BnumConfig.Get('local_keys')!.no_events!;
const EVENT_URL_TITLE_CLICK = 'bnum-card-agenda:title:url.click';
//#endregion Global constants

//#region Template
const TEMPLATE = (
	<HTMLBnumCardElement>
		<HTMLBnumCardTitle id={ID_CARD_TITLE} slot="title" data-icon="today">
			{TEXT_LAST_EVENTS}
		</HTMLBnumCardTitle>
		<HTMLBnumCardList>
			<slot></slot>
			<HTMLBnumCardItem id={ID_CARD_ITEM_NO_ELEMENTS} disabled hidden>
				{TEXT_NO_EVENTS}
			</HTMLBnumCardItem>
		</HTMLBnumCardList>
	</HTMLBnumCardElement>
);
//#endregion Template

/**
 * Organisme qui permet d'afficher simplement une liste d'évènements dans une carte.
 *
 * @category Card
 *
 * @structure Avec des éléments
 * <bnum-card-agenda>
 * <bnum-card-item-agenda
 *    data-date="2024-01-01"
 *    data-start-date="2024-01-01 08:00:00"
 *    data-end-date="2024-01-01 10:00:00"
 *    data-title="Réunion de projet"
 *    data-location="Salle de conférence">
 * </bnum-card-item-agenda>
 * <bnum-card-item-agenda
 *    data-date="2025-11-20"
 *    data-start-date="2025-10-20 09:40:00"
 *    data-end-date="2025-12-20 10:10:00"
 *    data-title="Réunion de projet"
 *    data-location="Salle de conférence">
 * </bnum-card-item-agenda>
 * <bnum-card-item-agenda all-day
 *    data-date="2025-11-21"
 *    data-title="Télétravail"
 *    data-location="A la maison">
 * </bnum-card-item-agenda>
 * </bnum-card-agenda>
 *
 * @structure Sans éléments
 * <bnum-card-agenda>
 * </bnum-card-agenda>
 *
 * @structure Avec une url
 * <bnum-card-agenda data-url="#">
 * </bnum-card-agenda>
 *
 * @slot (default) - Contenu des éléments de type HTMLBnumCardItemAgenda.
 *
 * @attr {string | undefined} (optional) data-url - Ajoute une url au titre. Ne rien mettre pour que l'option "url" du titre ne s'active pas.
 * @attr {string | undefined} (optional) loading - Si présent, affiche le mode loading.
 *
 * @event {CustomEvent<HTMLBnumCardItemAgenda[]>} bnum-card-agenda:change - Déclenché lorsque les éléments changent (ajout/suppression).
 *
 * @cssvar {block} --bnum-card-agenda - Définit le display du composant. Par défaut à "block".
 */
@Define({ tag: TAG_CARD_AGENDA, styles: style, template: TEMPLATE })
@Observe(ATTRIBUTE_LOADING)
export class HTMLBnumCardAgenda extends BnumElement {
	//#region Private fields
	#_isSorting: boolean = false;
	#_card: Nullable<HTMLBnumCardElement> = null;

	//#endregion Private fields

	//#region Getters/Setters
	@UI({
		cardTitle: `#${ID_CARD_TITLE}`,
		slot: 'slot',
		noElements: `#${ID_CARD_ITEM_NO_ELEMENTS}`,
	})
	accessor #_ui!: {
		cardTitle: HTMLBnumCardTitle;
		slot: HTMLSlotElement;
		noElements: HTMLBnumCardItem;
	};

	/**
	 * Déclenché lorsque les éléments changent (ajout/suppression).
	 */
	@Listener(onElementChangedInitializer)
	accessor onElementChanged!: OnElementChangedEvent;

	@Attr()
	accessor loading: boolean = false;

	@Data() accessor #_url: string = EMPTY_STRING;
	@Data() accessor #_max: Optional<Nullable<number>>;

	get #_cardPart(): HTMLBnumCardElement {
		if (this.#_card === null) {
			this.#_card =
				this.querySelector?.(HTMLBnumCardElement.TAG) ??
				this.shadowRoot?.querySelector?.(HTMLBnumCardElement.TAG) ??
				null;
		}
		return this.#_card!;
	}

	/**
	 * Nombre maximum d'éléments à afficher.
	 */
	get max(): Nullable<number> {
		return this.#_max ?? null;
	}

	/**
	 * Nombre maximum d'éléments à afficher.
	 */
	set max(value: Nullable<number>) {
		this.#_max = value;

		const items = this.#_getItems();

		if (this.alreadyLoaded) {
			if (value === null) this.#_showAllItems(items);
			else this.#_hideMax(items);
		}
	}

	//#endregion Getters/Setters
	//#region Lifecycle
	constructor() {
		super();
	}

	protected _p_attach(): void {
		if (this.#_url !== EMPTY_STRING) {
			this.#_ui.cardTitle.url = this.#_url;
			this.#_ui.cardTitle.onurlclick.add(EVENT_DEFAULT, e => {
				this.trigger(
					EVENT_URL_TITLE_CLICK,
					{ inner: e },
					{ bubbles: e.bubbles, cancelable: e.cancelable },
				);
			});
		}

		// On écoute les changements dans le slot (Items statiques ou ajoutés via JS)
		this.#_ui.slot.addEventListener(
			'slotchange',
			this.#_handleSlotChange.bind(this),
		);

		this.#_handleSlotChange();

		if (this.loading) this.#_setLoading();
	}

	protected _p_update(
		name: string,
		_: string | null,
		newVal: string | null,
	): void | Nullable<'break'> {
		switch (name) {
			case ATTRIBUTE_LOADING:
				if (newVal === null) this.#_cardPart.removeAttribute(ATTRIBUTE_LOADING);
				else this.#_setLoading({ attributeValue: newVal });
				break;
		}
	}
	//#endregion Lifecycle

	//#region Public methods
	/**
	 * Retire la limite du nombre d'éléments.
	 */
	public removeMax(): this {
		if (this.max !== null) this.max = null;
		return this;
	}

	/**
	 * Ajoute des éléments.
	 *
	 * Note: On ajoute simplement au Light DOM. Le slotchange détectera l'ajout et déclenchera le tri.
	 * @param content Elements à ajouter
	 */
	public add(...content: HTMLBnumCardItemAgenda[]): this {
		this.append(...content);
		return this;
	}

	/**
	 * Vide le composant.
	 */
	public clear(): this {
		this.innerHTML = EMPTY_STRING; // Vide le Light DOM
		return this;
	}
	//#endregion Public methods

	//#region Private methods
	/**
	 * Met la card en mode loading
	 * @param param0
	 */
	#_setLoading({
		attributeValue = EMPTY_STRING,
	}: { attributeValue?: string | boolean } = {}) {
		this.#_cardPart.setAttribute(
			ATTRIBUTE_LOADING,
			(attributeValue as unknown as string) ?? EMPTY_STRING,
		);
	}

	/**
	 * Gère le tri des éléments.
	 * Utilise requestAnimationFrame pour ne pas bloquer le thread si beaucoup d'items.
	 */
	#_handleSlotChange(): void {
		if (this.#_isSorting) return;

		this.#_sortChildren();
	}

	/**
	 * Tri les éléments enfants de la liste par date décroissante.
	 */
	@RenderFrame()
	#_sortChildren(): void {
		const agendaItems = this.#_getItems();

		if (agendaItems.length === 0) {
			this.#_ui.noElements.hidden = false;
			this.#_ui.slot.hidden = true;
			return;
		} else {
			this.#_ui.noElements.hidden = true;
			this.#_ui.slot.hidden = false;
		}

		if (agendaItems.length < 2) return; // Pas besoin de trier

		// 2. Vérifier si un tri est nécessaire (optimisation)
		let isSorted = true;
		for (let i = 0; i < agendaItems.length - 1; i++) {
			if (this.#_getDate(agendaItems[i]) < this.#_getDate(agendaItems[i + 1])) {
				isSorted = false;
				break;
			} else if (
				this.#_getDate(agendaItems[i]) === this.#_getDate(agendaItems[i + 1])
			) {
				// Même date de base, on regardmailItemse la date de début
				if (
					this.#_getStartDate(agendaItems[i]) <
					this.#_getStartDate(agendaItems[i + 1])
				) {
					isSorted = false;
					break;
				}
			}
		}
		if (isSorted) return;

		this.#_isSorting = true; // Verrouiller pour éviter que le déplacement ne relance slotchange

		// Réinsérer dans l'ordre via un Fragment (1 seul Reflow)
		const fragment = document.createDocumentFragment();
		const sortedItems = ArrayUtils.sortByDatesDescending(
			agendaItems,
			x => this.#_getDate(x),
			x => this.#_getStartDate(x),
		);

		fragment.append(...sortedItems);
		this.appendChild(fragment); // Déplace les éléments existants, ne les recrée pas.

		this.#_hideMax(sortedItems);

		// Notifier le changement
		this.onElementChanged.call(agendaItems);

		// Déverrouiller après que le microtask de mutation soit passé
		setTimeout(() => {
			this.#_isSorting = false;
		}, 0);
	}

	#_hideMax(agendaItems: HTMLBnumCardItemAgenda[]) {
		if (this.max === null) return;

		for (let index = 0, len = agendaItems.length; index < len; ++index) {
			const element = agendaItems[index];

			if (element.hasClass('last')) element.removeClass('last');

			if (index >= this.max) element.hidden = true;
			else {
				element.hidden = false;

				if (index === this.max - 1) element.addClass('last');
			}
		}
	}

	#_showAllItems(agendaItems: HTMLBnumCardItemAgenda[]) {
		for (const item of agendaItems) {
			item.hidden = false;
		}
	}

	#_getItems() {
		// Récupérer les éléments assignés au slot
		const elements = this.#_ui.slot.assignedElements() as HTMLElement[];

		// Filtrer pour être sûr de ne trier que des événements (sécurité)
		return elements.filter(el =>
			el.tagName.toLowerCase().includes(HTMLBnumCardItemAgenda.TAG),
		) as HTMLBnumCardItemAgenda[];
	}

	/**
	 * Helper pour parser la date de manière robuste
	 */
	#_getDate(item: HTMLBnumCardItemAgenda): number {
		return item.baseDate.getTime();
	}

	/**
	 * Helper pour parser la date de manière robuste
	 */
	#_getStartDate(item: HTMLBnumCardItemAgenda): number {
		return item.isAllDay ? this.#_getDate(item) : item.startDate.getTime();
	}
	//#endregion Private methods

	//#region Static methods
	/**
	 * Méthode statique pour créer une instance du composant.
	 * @param param0 Options de création
	 * @param param0.contents Contenus initiaux à ajouter
	 * @param param0.url URL du titre
	 * @returns Nouvelle node HTMLBnumCardAgenda
	 */
	static Create({
		contents = [],
		url = EMPTY_STRING,
	}: {
		contents?: HTMLBnumCardItemAgenda[];
		url?: string;
	} = {}): HTMLBnumCardAgenda {
		const node = document.createElement(this.TAG) as HTMLBnumCardAgenda;

		if (url !== EMPTY_STRING) node.setAttribute(ATTRIBUTE_DATA_URL, url);
		if (contents.length > 0) node.add(...contents);

		return node;
	}

	/**
	 * Liste des évènements disponibles pour cet éléments
	 */
	static get Events(): Readonly<{
		TITLE_URL_CLICKED: Readonly<typeof EVENT_URL_TITLE_CLICK>;
		CHANGE: Readonly<typeof CHANGE_EVENT>;
	}> {
		return {
			TITLE_URL_CLICKED: EVENT_URL_TITLE_CLICK,
			CHANGE: CHANGE_EVENT,
		};
	}

	//#endregion Static methods
}
